import { motion } from 'framer-motion';
import { Check, Star, Clock, Users, Shield, Zap, HeadphonesIcon } from 'lucide-react';
import { useState } from 'react';
import { OptimizedHeroSection, heroImages } from '../utils/heroImages';
import toast from 'react-hot-toast';

const Support = () => {
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });
  const [selectedPlanForPurchase, setSelectedPlanForPurchase] = useState(null);
  
  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: '$999',
      priceValue: 999.00,
      period: '/month',
      description: 'Ideal for small businesses and startups',
      features: [
        'Business hours support (9AM-5PM, Mon-Fri)',
        'Email support with 48hr response',
        'Basic database health monitoring',
        'Monthly system reports',
        'Access to knowledge base & tutorials',
        'Community forum access',
        'Basic SLA (99% uptime)',
        'Up to 5 support tickets per month',
      ],
      recommended: false,
      color: 'from-green-500 to-green-600',
    },
    {
      id: 'standard',
      name: 'Standard',
      price: '$2,500',
      priceValue: 2500.00,
      period: '/month',
      description: 'Perfect for small to medium businesses',
      features: [
        'Business hours support (9AM-5PM)',
        'Email support with 24hr response',
        'Basic monitoring & alerts',
        'Monthly health checks',
        'Database backup verification',
        'Access to knowledge base',
        'Standard SLA (99.5% uptime)',
        'Up to 20 support tickets per month',
        'Basic performance tuning',
      ],
      recommended: false,
      color: 'from-gray-500 to-gray-600',
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$5,000',
      priceValue: 5000.00,
      period: '/month',
      description: 'Most popular for growing companies',
      features: [
        'Extended support (7AM-9PM)',
        'Priority email & phone support',
        'Advanced monitoring & alerting',
        'Weekly health checks',
        'Automated backup solutions',
        'Performance optimization',
        'Enhanced SLA (99.9% uptime)',
        'Dedicated account manager',
        'Unlimited support tickets',
        'Quarterly strategy sessions',
      ],
      recommended: true,
      color: 'from-azellar-navy to-azellar-teal',
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '$10,000',
      priceValue: 10000.00,
      period: '/month',
      description: 'For mission-critical applications',
      features: [
        '24/7 support & monitoring',
        'Dedicated support team',
        'Real-time monitoring & alerts',
        'Daily health checks',
        'Automated failover systems',
        'Custom optimization plans',
        'Premium SLA (99.99% uptime)',
        'On-site support available',
        'Custom integrations',
        'Unlimited priority support',
        'Monthly executive reviews',
      ],
      recommended: false,
      color: 'from-azellar-teal to-azellar-cyan',
    },
  ];

  const handlePlanPurchase = async (plan) => {
    setPaymentLoading(plan.id);
    
    try {
      // Create Stripe checkout session for support plan
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/v1/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: plan.id,
          plan_name: plan.name,
          amount: plan.priceValue,
          origin_url: window.location.origin,
          customer_email: customerInfo.email,
          customer_name: customerInfo.name,
          company_name: customerInfo.company || `${customerInfo.name}'s Company`,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment. Please try again.');
    } finally {
      setPaymentLoading(null);
    }
  };

  const handleCustomerInfoChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleCustomerInfoSubmit = async (e) => {
    e.preventDefault();
    
    if (!customerInfo.name || !customerInfo.email) {
      toast.error('Please fill in all required fields.');
      return;
    }
    
    setShowCustomerModal(false);
    await handlePlanPurchase(selectedPlanForPurchase);
  };

  const [formData, setFormData] = useState({
    summary: '',
    description: '',
    severity: 'medium',
    component: '',
    email: '',
    phone: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.email,
          email: formData.email,
          message: `Support Inquiry - ${formData.summary}\n\nSeverity: ${formData.severity}\nComponent: ${formData.component}\nPhone: ${formData.phone}\n\nDescription:\n${formData.description}`,
          inquiry_type: 'support',
        }),
      });

      if (response.ok) {
        toast.success('Support inquiry submitted successfully!');
        setFormData({
          summary: '',
          description: '',
          severity: 'medium',
          component: '',
          email: '',
          phone: '',
        });
      } else {
        throw new Error('Failed to submit inquiry');
      }
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-azellar-light via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Hero Section */}
      <OptimizedHeroSection imageUrl={heroImages.support}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              Support <span className="bg-gradient-to-r from-azellar-aqua to-azellar-cyan bg-clip-text text-transparent">Plans</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto">
              Choose the perfect support level for your business needs with our comprehensive maintenance and monitoring services.
            </p>
          </motion.div>
        </div>
      </OptimizedHeroSection>

      {/* Support Plans */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Choose Your <span className="gradient-text">Support Level</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Our support plans ensure your database systems run smoothly with proactive monitoring and expert assistance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 ${
                  plan.recommended ? 'border-azellar-teal' : 'border-gray-200 dark:border-gray-600'
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-azellar-navy to-azellar-teal text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold gradient-text">{plan.price}</span>
                    <span className="text-gray-500 dark:text-gray-400 ml-2">{plan.period}</span>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center">
                      <Check className="w-5 h-5 text-azellar-teal mr-3 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => {
                    setSelectedPlanForPurchase(plan);
                    setShowCustomerModal(true);
                  }}
                  disabled={paymentLoading === plan.id}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                    plan.recommended
                      ? 'btn-primary'
                      : 'btn-outline hover:bg-azellar-teal hover:text-white'
                  } ${paymentLoading === plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {paymentLoading === plan.id ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    `Choose ${plan.name}`
                  )}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Form */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Get <span className="gradient-text">Support</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Need immediate help? Submit a support ticket and our team will get back to you promptly.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Issue Summary
                </label>
                <input
                  type="text"
                  name="summary"
                  value={formData.summary}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  placeholder="Brief description of your issue"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Severity
                  </label>
                  <select
                    name="severity"
                    value={formData.severity}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Component
                  </label>
                  <select
                    name="component"
                    value={formData.component}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  >
                    <option value="">Select Component</option>
                    <option value="database">Database</option>
                    <option value="performance">Performance</option>
                    <option value="backup">Backup & Recovery</option>
                    <option value="security">Security</option>
                    <option value="monitoring">Monitoring</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Detailed Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  placeholder="Please provide detailed information about your issue, including error messages, steps to reproduce, and any relevant context..."
                />
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="btn-primary text-lg px-8 py-4"
                >
                  Submit Support Ticket
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-azellar-navy to-azellar-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Need Help Choosing?
            </h2>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Contact our sales team for a personalized consultation and support plan recommendation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a
                href="/contact"
                className="btn-secondary text-lg px-8 py-4 shadow-glow"
              >
                Contact Sales
              </a>
              <a
                href="/faq"
                className="btn-outline text-white border-white hover:bg-white hover:text-azellar-navy text-lg px-8 py-4"
              >
                View FAQ
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Customer Information Modal */}
      {showCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-2xl"
          >
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Complete Your Purchase
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Please provide your information to create your support account.
              </p>
              {selectedPlanForPurchase && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Selected Plan:</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {selectedPlanForPurchase.name} - {selectedPlanForPurchase.price}{selectedPlanForPurchase.period}
                  </p>
                </div>
              )}
            </div>

            <form onSubmit={handleCustomerInfoSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={customerInfo.name}
                  onChange={handleCustomerInfoChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={customerInfo.email}
                  onChange={handleCustomerInfoChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  name="company"
                  value={customerInfo.company}
                  onChange={handleCustomerInfoChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  placeholder="Enter your company name (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={customerInfo.phone}
                  onChange={handleCustomerInfoChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  placeholder="Enter your phone number (optional)"
                />
              </div>

              <div className="flex space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCustomerModal(false);
                    setSelectedPlanForPurchase(null);
                  }}
                  className="flex-1 py-3 px-6 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={paymentLoading}
                  className="flex-1 py-3 px-6 btn-primary font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {paymentLoading ? 'Processing...' : 'Continue to Payment'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Support;
